import { useLayoutEffect, useRef } from 'react';

/** Scroll only the dialog body. Layout offsets remain valid during dock transforms. */
export function useInspectorScroll(open: boolean, navigation: { name: string; smooth: boolean; sequence: number }) {
  const sheetsRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const sheets = sheetsRef.current!;
    const target = Array.from(sheets.children).find(node =>
      (node as HTMLElement).dataset.investigator?.toLowerCase() === navigation.name.toLowerCase()) as HTMLElement | undefined;
    const body = sheets.closest<HTMLElement>('.game-dialog-body');
    if (!target || !body) return;
    const dialog = body.closest<HTMLElement>('.game-detail-dialog')!;
    let stopped = false;
    let lastTop = -1;
    const align = (smooth = false) => {
      if (stopped) return;
      const top = target.offsetTop - parseFloat(getComputedStyle(body).paddingTop || '0');
      if (top === lastTop) return;
      lastTop = top;
      body.scrollTo?.({ top, behavior: smooth && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto' });
    };
    align(navigation.smooth);
    // Deferred predecessors and loading images can change the target's offset.
    // Stop correcting as soon as the user resumes interacting with the dialog.
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(() => align());
    Array.from(sheets.children).forEach(node => observer?.observe(node));
    const realign = () => align();
    const stop = () => { stopped = true; observer?.disconnect(); };
    sheets.addEventListener('load', realign, true);
    sheets.addEventListener('error', realign, true);
    const inputEvents = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;
    inputEvents.forEach(type => dialog.addEventListener(type, stop, { passive: true }));
    body.addEventListener('focusin', stop);
    return () => {
      stop();
      sheets.removeEventListener('load', realign, true);
      sheets.removeEventListener('error', realign, true);
      inputEvents.forEach(type => dialog.removeEventListener(type, stop));
      body.removeEventListener('focusin', stop);
    };
  }, [open, navigation]);
  return sheetsRef;
}
